import * as R from 'ramda';
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  useGetKitCombinations,
  useGetKitCombinationsCount,
} from '../../../../../../services/modules/kit/kitDetails/kitDetails.queries';
import useProductDetailsStore from '../../../../../../stores/ProductDetails/productDetails.store';
import { ProductDetailsOriginType } from '../../../../../../stores/ProductDetails/productDetails.types';
import useIsAdminSeller from '../../../../../../stores/ShowUnavailableProducts/hooks/useIsAdminSeller';
import { useApplicationContext } from '../../../../../context/ApplicationContext/ApplicationContext';
import { AvailabilityEnum } from '../../../../ProductTypes';
import { mapKitDetailsListGroups } from '../utils/kitDetailsList.utils';

const useKitDetailsList = () => {
  const { team } = useApplicationContext();
  const isAdminSeller = useIsAdminSeller();
  const { productDetailModalState, productGridParams, from } = useProductDetailsStore(
    useShallow((state) => ({
      productDetailModalState: state.productDetailsModalState,
      productGridParams: state.productGridParams,
      from: state.from,
    })),
  );
  const catalogId = from?.type === ProductDetailsOriginType.CATALOG ? from.id : undefined;

  const {
    data: kitCombinationsCount,
    isLoading: isCountLoading,
    refetch: refetchGetKitCombinationsCount,
  } = useGetKitCombinationsCount(
    {
      kitId: productDetailModalState.kitId!,
      filters: {
        limit: 15,
        skip: 0,
        catalogId,
        isAdminSeller,
        orderIds: productGridParams?.orderIds,
        availabilities: productGridParams?.availability as AvailabilityEnum[] | undefined,
      },
    },
    {
      enabled: !!productDetailModalState.kitId,
    },
  );

  const {
    data: combinationsData,
    isLoading: isCombinationsLoading,
    fetchNextPage,
    hasNextPage,
    refetch: refetchGetKitCombinations,
  } = useGetKitCombinations({
    teamId: team?.id,
    totalCount: R.sum(kitCombinationsCount?.map((item) => item.count) ?? []),
    kitId: productDetailModalState.kitId!,
    filters: {
      limit: 15,
      skip: 0,
      catalogId,
      isAdminSeller,
      orderIds: productGridParams?.orderIds,
      availabilities: productGridParams?.availability as AvailabilityEnum[] | undefined,
    },
    options: {
      enabled: !!productDetailModalState.kitId && !!kitCombinationsCount?.length,
    },
  });

  const combinations = useMemo(
    () => combinationsData?.pages.flatMap((page) => page.data) ?? [],
    [combinationsData?.pages],
  );

  const groups = useMemo(() => mapKitDetailsListGroups(combinations), [combinations]);

  const groupCounts = groups.map((group) => group.count);

  const handleNextPage = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  return {
    groups,
    groupCounts,
    combinations,
    kitCombinationsCount,
    isLoading: isCountLoading || isCombinationsLoading,
    handleNextPage,
    refetchGetKitCombinations,
    refetchGetKitCombinationsCount,
  };
};

export default useKitDetailsList;
